import { removeWmsLayer } from '../../../common/functions/remove_wms_layer.function';
import mapboxgl from 'mapbox-gl';
mapboxgl.accessToken = process.env.REACT_APP_PUBLIC_MAP_BOX_GL_ACCESS_TOKEN;

const vendorColorMap = {
  jazz: '#EF4444',
  wateen: '#2563EB',
  mppl: '#8B5CF6',
  ptcl: '#10B981',
  wtl: '#F97316',
  nayatel: '#CA8A04',
};

const formatTooltipValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '--';
  }
  return value;
};

const formatTooltipDate = (value) => {
  if (!value) {
    return '--';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const parseLengthValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string') {
    const numericString = value.replace(/[^0-9.-]/g, '');
    if (!numericString) {
      return undefined;
    }
    const parsed = Number(numericString);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const resolveInventoryLength = (source = {}) => parseLengthValue(source.length);

const normalizeTenancyValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'own') {
    return 'owned';
  }
  if (normalized === 'iru') {
    return 'leased';
  }
  return normalized;
};

const formatTenancyDisplay = (value) => {
  const normalized = normalizeTenancyValue(value);
  if (normalized === 'owned') {
    return 'Owned';
  }
  if (normalized === 'leased') {
    return 'Leased';
  }
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return value;
};

const formatLength = (value) => {
  const num = parseLengthValue(value);
  if (Number.isFinite(num)) {
    return `${num.toFixed(2)} KM`;
  }
  return '--';
};

export async function addWmsLayer(
  map,
  currentKmzNetwork,
  popupRef,
  stepRef,
  showBackdrop,
  hideBackdrop,
  inventoryData,
  fiberVisibility,
  geoJsonData
) {
  showBackdrop();
  try {
    if (!map.current) {
      hideBackdrop();
      return;
    }

    // Always remove existing layers first to prevent flickering
    if (map.current.getLayer('linksLayers')) {
      map.current.removeLayer('linksLayers');
    }
    if (map.current.getLayer('linksLayers-dashed')) {
      map.current.removeLayer('linksLayers-dashed');
    }
    if (map.current.getSource('linksLayers')) {
      map.current.removeSource('linksLayers');
    }

    // Only proceed if source doesn't exist (double-check after removal)
    if (!map.current.getSource('linksLayers')) {
      const data = geoJsonData;

      if (!data) {
        console.log('No geojson data available for links layer');
        return;
      }

      const getLayerKey = (value) => (value ? String(value).trim().toLowerCase() : '');
      const inventoryByLayer = new Map();
      (inventoryData || []).forEach((item) => {
        const rawTenancy = item.fiber_tenancy_type ?? item.tenancy_type ?? '';
        const normalizedTenancy = normalizeTenancyValue(item.tenancy_type_normalized ?? rawTenancy);
        const displayTenancy = formatTenancyDisplay(rawTenancy);
        const normalizedItem = {
          ...item,
          fiber_tenancy_type: displayTenancy,
          tenancy_type: displayTenancy,
          tenancy_type_normalized: normalizedTenancy,
        };
        const layerKey = getLayerKey(item.layer_name);
        if (layerKey && !inventoryByLayer.has(layerKey)) {
          inventoryByLayer.set(layerKey, normalizedItem);
        }
        const trailKey = getLayerKey(item.trail_name);
        if (trailKey && !inventoryByLayer.has(trailKey)) {
          inventoryByLayer.set(trailKey, normalizedItem);
        }
      });

      const deriveAttributes = (properties = {}) => {
        const layerName =
          properties.layer_name ??
          properties.layerName ??
          properties.trail_name ??
          properties.trailName;
        const layerKey = getLayerKey(layerName);
        const matched = inventoryByLayer.get(layerKey);
        const tenancyCandidates = [
          matched?.fiber_tenancy_type,
          matched?.tenancy_type,
          properties?.fiber_tenancy_type,
          properties?.tenancy_type,
        ];
        const normalizedTenancyTypeFallback =
          tenancyCandidates.map((value) => normalizeTenancyValue(value)).find((value) => value) ||
          '';
        const normalizedTenancyType =
          normalizeTenancyValue(matched?.tenancy_type_normalized) || normalizedTenancyTypeFallback;
        const propertyIsRow =
          properties?.is_row !== undefined ? String(properties.is_row).toLowerCase().trim() : '';
        const isRow =
          typeof matched?.is_row === 'boolean'
            ? matched.is_row
            : properties?.is_row === true ||
              propertyIsRow === 'true' ||
              propertyIsRow === '1' ||
              normalizedTenancyType === 'row';
        const vendor = (matched?.vendor ?? properties?.vendor ?? '').toString().trim() || 'Unknown';
        const tenancyLabelRaw =
          matched?.fiber_tenancy_type ??
          matched?.tenancy_type ??
          properties?.fiber_tenancy_type ??
          properties?.tenancy_type ??
          '';
        const tenancyLabel = formatTenancyDisplay(tenancyLabelRaw);

        return { matched, normalizedTenancyType, tenancyLabel, isRow, vendor, layerKey };
      };

      // Extract features
      const allFeatures = data.features || [];

      // For OTN and MPBN networks, show all links regardless of fiber visibility
      const isOTNOrMPBN = currentKmzNetwork === 'OTN' || currentKmzNetwork === 'MPBN';

      const filteredFeatures = allFeatures.filter((feature) => {
        // For OTN and MPBN, show all features
        if (isOTNOrMPBN) {
          return true;
        }

        const props = feature.properties || {};
        const { normalizedTenancyType, isRow } = deriveAttributes(props);

        const isOwned =
          normalizedTenancyType === 'owned' ||
          normalizedTenancyType === 'myfiber' ||
          normalizedTenancyType === 'my fiber';
        const isLeased = normalizedTenancyType === 'leased';

        const showMy = fiberVisibility.myOwnedFiber;
        const showLeased = fiberVisibility.myLeasedFiber;
        const showRow = fiberVisibility.myRowFiber;

        // CASE: All filters are on → show everything
        if (showMy && showLeased && showRow) {
          return true;
        }

        // CASE: Only myFiber
        if (showMy && !showLeased && !showRow) {
          return isOwned && !isRow;
        }

        // CASE: Only leasedFiber
        if (!showMy && showLeased && !showRow) {
          return isLeased && !isRow;
        }

        // CASE: Only rowFiber
        if (!showMy && !showLeased && showRow) {
          return isRow === true;
        }

        // CASE: myFiber + rowFiber
        if (showMy && !showLeased && showRow) {
          return isOwned && isRow === true;
        }

        // CASE: leasedFiber + rowFiber
        if (!showMy && showLeased && showRow) {
          return isLeased;
        }

        // CASE: myFiber + leasedFiber (but no row)
        if (showMy && showLeased && !showRow) {
          return (isOwned || isLeased) && isRow === false;
        }

        // CASE: no filters on
        return false;
      });

      const coloredFeatures = filteredFeatures.map((feature) => {
        const props = feature.properties || {};

        const { matched, vendor, tenancyLabel, normalizedTenancyType, isRow } =
          deriveAttributes(props);

        const vendorKey = vendor.toLowerCase();
        const color = vendorColorMap[vendorKey] || '#9CA3AF';

        return {
          ...feature,
          properties: {
            ...props,
            assignedColor: color,
            fiber_tenancy_type: tenancyLabel,
            tenancy_type: tenancyLabel,
            tenancy_type_normalized: normalizedTenancyType,
            is_row: isRow,
            vendor,
            inventory_vendor: matched?.vendor ?? vendor,
            tenancy_label: tenancyLabel,
          },
        };
      });

      const filteredData = {
        type: 'FeatureCollection',
        features: coloredFeatures,
      };

      const isOnlyRow =
        !fiberVisibility.myOwnedFiber &&
        !fiberVisibility.myLeasedFiber &&
        fiberVisibility.myRowFiber;

      const isOnlyLeasedFiber =
        !fiberVisibility.myOwnedFiber &&
        fiberVisibility.myLeasedFiber &&
        !fiberVisibility.myRowFiber;

      map.current.addSource('linksLayers', {
        type: 'geojson',
        data: filteredData,
      });

      let linePaint;

      // For OTN and MPBN, use vendor colors (assignedColor) when available, otherwise default to green
      if (isOTNOrMPBN) {
        linePaint = {
          'line-color': 'green',
          'line-opacity': 1,
          'line-width': 3,
        };
      } else if (isOnlyRow) {
        linePaint = {
          'line-color': '#6E6E6E',
          'line-opacity': 1,
          'line-width': 3,
        };
      } else if (isOnlyLeasedFiber) {
        linePaint = {
          'line-color': ['get', 'assignedColor'],
          'line-opacity': 1,
          'line-width': 3,
        };
      } else {
        linePaint = {
          'line-color': 'green',
          'line-opacity': 1,
          'line-width': 3,
        };
      }

      map.current.addLayer({
        id: 'linksLayers',
        type: 'line',
        source: 'linksLayers',
        paint: linePaint,
      });

      let dashedPaint;

      // For OTN and MPBN, use vendor colors (assignedColor) when available, otherwise default to green
      if (isOTNOrMPBN) {
        dashedPaint = {
          'line-color': 'green',
          'line-width': 3,
          'line-dasharray': [0, 4, 3],
        };
      } else if (isOnlyRow) {
        dashedPaint = {
          'line-color': '#6E6E6E',
          'line-width': 3,
          'line-dasharray': [0, 4, 3],
        };
      } else if (isOnlyLeasedFiber) {
        dashedPaint = {
          'line-color': ['get', 'assignedColor'],
          'line-width': 3,
          'line-dasharray': [0, 4, 3],
        };
      } else {
        dashedPaint = {
          'line-color': 'green',
          'line-width': 3,
          'line-dasharray': [0, 4, 3],
        };
      }

      map.current.addLayer({
        id: 'linksLayers-dashed',
        type: 'line',
        source: 'linksLayers',
        paint: dashedPaint,
      });

      const dashArraySequence = [
        [0, 4, 3],
        [0.5, 4, 2.5],
        [1, 4, 2],
        [1.5, 4, 1.5],
        [2, 4, 1],
        [2.5, 4, 0.5],
        [3, 4, 0],
        [0, 0.5, 3, 3.5],
        [0, 1, 3, 3],
        [0, 1.5, 3, 2.5],
        [0, 2, 3, 2],
        [0, 2.5, 3, 1.5],
        [0, 3, 3, 1],
        [0, 3.5, 3, 0.5],
      ];

      const animateDashArray = (timestamp) => {
        const newStep = parseInt((timestamp / 120) % dashArraySequence.length);

        map.current.setPaintProperty(
          'linksLayers-dashed',
          'line-dasharray',
          dashArraySequence[stepRef.current]
        );
        stepRef.current = newStep;

        requestAnimationFrame(animateDashArray);
      };

      // animateDashArray(0);
      if (map?.current.getLayer('linksLayers-dashed')) {
        animateDashArray(0);
      }

      map.current.on('mouseenter', 'linksLayers', () => {
        map.current.getCanvasContainer().style.cursor = 'pointer';
      });
      map.current.on('mouseenter', 'linksLayers', (e) => {
        const feature = map.current.queryRenderedFeatures(e.point, {
          layers: ['linksLayers'],
        })[0];

        if (!feature) {
          return;
        }

        const properties = feature.properties || {};
        const lng = e.lngLat;

        const { matched, isRow } = deriveAttributes(properties);
        const inventoryLength = resolveInventoryLength(matched);
        const tooltipLengthValue = inventoryLength;
        const mergedData = {
          ...properties,
          ...matched,
        };

        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }

        const isOnlyRow =
          !fiberVisibility.myOwnedFiber &&
          !fiberVisibility.myLeasedFiber &&
          fiberVisibility.myRowFiber;

        const tooltipHtml = `
          <div class="mapTooltip-container">
            <div class="title_container_tooltip">
              <div class="title_text_container_tooltip">
                <h3 class="tooltip-heading">${formatTooltipValue(mergedData.layer_name)}</h3>
                <p class="tooltip-item"><strong>Vendor:</strong> ${formatTooltipValue(
                  mergedData.vendor ?? mergedData.inventory_vendor
                )}</p>
                ${
                  isOnlyRow || isRow
                    ? `
                      <p class="tooltip-item"><strong>Source Site:</strong> ${formatTooltipValue(
                        mergedData.source_site_code
                      )}</p>
                      <p class="tooltip-item"><strong>Sink Site:</strong> ${formatTooltipValue(
                        mergedData.sink_site_code
                      )}</p>
                      <p class="tooltip-item"><strong>Commission Date:</strong> ${formatTooltipDate(
                        mergedData.commission_date ?? mergedData.commissioned_at
                      )}</p>
                      <p class="tooltip-item"><strong>Length:</strong> ${formatLength(tooltipLengthValue)}</p>
                      <p class="tooltip-item"><strong>Lease Start:</strong> ${formatTooltipDate(
                        mergedData.lease_start_date
                      )}</p>
                      <p class="tooltip-item"><strong>Lease End:</strong> ${formatTooltipDate(
                        mergedData.lease_end_date
                      )}</p>
                      <p class="tooltip-item"><strong>Concern Dept:</strong> ${formatTooltipValue(
                        mergedData.concern_department
                      )}</p>
                      <p class="tooltip-item"><strong>POC:</strong> ${formatTooltipValue(
                        mergedData.poc
                      )}</p>
                      <p class="tooltip-item"><strong>POC Phone:</strong> ${formatTooltipValue(
                        mergedData.poc_phone
                      )}</p>
                    `
                    : `
                      <p class="tooltip-item"><strong>Fiber Type:</strong> ${formatTooltipValue(
                        mergedData.fiber_type
                      )}</p>
                      <p class="tooltip-item"><strong>Fiber Pair:</strong> ${formatTooltipValue(
                        mergedData.fiber_pair ?? mergedData.pair_count
                      )}</p>
                      <p class="tooltip-item"><strong>Used Pair:</strong> ${formatTooltipValue(
                        mergedData.used_pair
                      )}</p>
                      <p class="tooltip-item"><strong>Available Pair:</strong> ${formatTooltipValue(
                        mergedData.available_pair ?? mergedData.unused_pair
                      )}</p>
                      <p class="tooltip-item"><strong>Link Rate (Gbps):</strong> ${formatTooltipValue(
                        mergedData.link_rate_gbps ?? mergedData.link_rate
                      )}</p>
                      <p class="tooltip-item"><strong>Length:</strong> ${formatLength(tooltipLengthValue)}</p>
                      <p class="tooltip-item"><strong>Tenancy Type:</strong> ${formatTooltipValue(
                        mergedData.fiber_tenancy_type ?? mergedData.tenancy_type
                      )}</p>
                      <p class="tooltip-item"><strong>Commission Date:</strong> ${formatTooltipDate(
                        mergedData.commission_date ?? mergedData.commissioned_at
                      )}</p>
                      <p class="tooltip-item"><strong>Scope Year:</strong> ${formatTooltipValue(
                        mergedData.scope_year
                      )}</p>
                      <p class="tooltip-item"><strong>Region:</strong> ${formatTooltipValue(
                        mergedData.source_region ?? mergedData.region
                      )}</p>
                      <p class="tooltip-item"><strong>City:</strong> ${formatTooltipValue(
                        mergedData.source_city ?? mergedData.city
                      )}</p>
                      <p class="tooltip-item"><strong>Ring:</strong> ${formatTooltipValue(
                        mergedData.source_ring ?? mergedData.ring_unique_name
                      )}</p>
                      <p class="tooltip-item"><strong>Source Site:</strong> ${formatTooltipValue(
                        mergedData.source_site_code
                      )}</p>
                      <p class="tooltip-item"><strong>Sink Site:</strong> ${formatTooltipValue(
                        mergedData.sink_site_code
                      )}</p>
                      <p class="tooltip-item"><strong>Link Type:</strong> ${formatTooltipValue(
                        mergedData.link_type
                      )}</p>
                    `
                }
              </div>
            </div>
          </div>
        `;

        popupRef.current = new mapboxgl.Popup({ closeButton: false })
          .setLngLat(lng)
          .setHTML(tooltipHtml)
          .addTo(map.current);
      });

      map.current.on('mouseleave', 'linksLayers', () => {
        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }
      });

      map.current.on('mouseleave', 'linksLayers', () => {
        map.current.getCanvasContainer().style.cursor = '';
      });
    }
  } catch (error) {
    console.error('Error adding WFS layer:', error);
  }
  hideBackdrop();
}
